import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, EMPTY, throwError } from 'rxjs';
import { AppInsightsService } from 'src/app/core/services/app-insights.service';
import { AuthService } from '@auth0/auth0-angular';
import { nativeLogout, isNativeApp } from 'src/app/core/auth/native-auth';
import { HttpHeaders } from '@angular/common/http';
import { ToastService } from 'src/app/shared/utils/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { AppBadgeService } from 'src/app/core/services/app-badge.service';
import { ClientTelemetryContextService } from 'src/app/core/services/client-telemetry-context.service';
import { SILENT_5XX } from 'src/app/core/interceptors/http-context.tokens';
import { isLoginRequiredError, isInteractiveReauthRequiredError } from 'src/app/core/auth/reauth-error';
import { initiateInteractiveReauth } from 'src/app/core/auth/interactive-reauth';
import { rettxApiTimeoutDetails } from './http-timeout.interceptor';
import { ERROR_SCREEN_ARRIVAL_REASON_STATE_KEY } from 'src/app/core/domain/error-screen';

/**
 * HTTP status codes this interceptor treats as server-side failures worth a
 * dedicated redirect/telemetry path. This is the HANDLED SUBSET of the 5xx range
 * (500 Internal Server Error, 502 Bad Gateway, 503 Service Unavailable, 504
 * Gateway Timeout) — NOT every possible 5xx. Each of these gets a DEDICATED App
 * Insights exception (see below) so the failure stays diagnosable even after the
 * `/page-error-500` navigation renames the operation under
 * `enableAutoRouteTracking` (spec 034 observability lineage).
 */
const SERVER_ERROR_STATUSES = [500, 502, 503, 504];

/**
 * Patient lifecycle capability error codes (Feature 011)
 * Maps backend error codes to translation keys
 */
const LIFECYCLE_ERROR_MAP: Record<string, string> = {
  'PATIENT_NOT_EDITABLE': 'PATIENT.LIFECYCLE.ERRORS.NOT_EDITABLE',
  'PATIENT_NOT_DELETABLE': 'PATIENT.LIFECYCLE.ERRORS.NOT_DELETABLE',
  'CORRECTION_NOT_ALLOWED': 'PATIENT.LIFECYCLE.ERRORS.CORRECTION_NOT_ALLOWED'
};

/**
 * Check if error is a patient lifecycle capability error
 * These are 403 errors that shouldn't navigate away but should show a user message
 */
function isLifecycleCapabilityError(error: HttpErrorResponse): boolean {
  if (error.status !== 403) return false;
  const errorCode = error?.error?.error || '';
  return errorCode in LIFECYCLE_ERROR_MAP;
}

/**
 * Get the translation key for a lifecycle error
 */
function getLifecycleErrorKey(error: HttpErrorResponse): string | null {
  const errorCode = error?.error?.error || '';
  return LIFECYCLE_ERROR_MAP[errorCode] || null;
}

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const appInsightsService = inject(AppInsightsService);
  const auth0 = inject(AuthService);
  const toastService = inject(ToastService);
  // Lazily resolve TranslateService (only needed in the error path below). Eagerly
  // `inject(TranslateService)` here re-enters DI during the very first i18n bundle
  // request — TranslateService kicks off that `en.json` load from its own
  // construction (defaultLanguage: 'en'), that request flows through this
  // interceptor, and injecting the still-constructing service triggers NG0200
  // (circular dependency) → the app falls into the global error page. Deferring
  // the lookup to error time breaks the cycle without changing behaviour.
  const injector = inject(Injector);
  const appBadge = inject(AppBadgeService);
  const telemetryContext = inject(ClientTelemetryContextService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // A `missing_refresh_token` OR `invalid_grant` ("Unknown or invalid
      // refresh token") failure is a broken-but-authenticated session: force a
      // real interactive re-login (full-page /authorize) that replaces the stale
      // cache. Navigating to `/login` here would loop (LegacyLoginRedirect -> `/`
      // -> publicOnlyGuard bounce), so we don't.
      if (isInteractiveReauthRequiredError(error)) {
        console.warn('[httpErrorInterceptor] Broken refresh-token session, forcing interactive re-login...');
        initiateInteractiveReauth(auth0);
        (error as any).__handled = true;
        (error as any).__loginRequired = true;
        return EMPTY;
      }

      // A `login_required` failure means the user is unauthenticated: redirect to
      // the `/login` route without error UI (unchanged from #190).
      if (isLoginRequiredError(error)) {
        console.warn('[httpErrorInterceptor] Session expired, redirecting to login...');
        const returnUrl = router.url;
        router.navigate(['/login'], {
          queryParams: returnUrl !== '/' && returnUrl !== '/login' ? { returnUrl } : {}
        });
        // Mark as handled and return EMPTY to prevent further error propagation
        (error as any).__handled = true;
        (error as any).__loginRequired = true;
        return EMPTY;
      }

      const timeoutDetails = rettxApiTimeoutDetails(error);
      if (timeoutDetails) {
        appInsightsService.trackException(
          new Error(`HTTP ${req.method} request timed out`),
          3,
          {
            source: 'httpTimeoutInterceptor',
            method: req.method,
            routeClass: timeoutDetails.routeClass,
            timeoutMs: timeoutDetails.timeoutMs
          }
        );
        (error as any).__handled = true;
        return throwError(() => error);
      }

      // Handle patient lifecycle capability errors (Feature 011)
      // These show a toast message but don't navigate away
      if (isLifecycleCapabilityError(error)) {
        const translationKey = getLifecycleErrorKey(error);
        if (translationKey) {
          const translate = injector.get(TranslateService);
          const message = translate.instant(translationKey);
          toastService.showToast(message, 'error');
          
          appInsightsService.trackTrace(
            'Patient lifecycle capability denied',
            {
              errorCode: error?.error?.error,
              currentState: error?.error?.details?.current_state,
              url: req.url,
              method: req.method
            }
          );
        }
        // Mark as handled but still throw to let service handle it
        (error as any).__handled = true;
        (error as any).__lifecycleError = true;
        return throwError(() => error);
      }

      // Flag passive/background/boot requests (badge polls, boot-time
      // reconciliation, silent refreshes) that opted in via the SILENT_5XX token
      // so downstream handling can suppress intrusive UX — the full-page
      // /page-error-500 redirect below AND the red error toast in
      // ErrorHandlerService — for failures the CALLER already recovers from,
      // WITHOUT losing telemetry. Defaults to false so all other requests keep
      // their current behaviour.
      const silent = req.context.get(SILENT_5XX);
      (error as any).__silent5xx = silent;

      // Log the error to Application Insights.
      //
      // 5xx server errors get a DEDICATED, RELIABLE, QUERYABLE record instead of
      // the generic one below. Two problems motivated this: (a) the generic call
      // passes the raw `HttpErrorResponse` — which is NOT an `Error` instance and
      // has no stack — so App Insights recorded an inconsistent/uninformative
      // exception signature; and (b) the exception is correlated to the operation
      // active at failure time, which differs from the `/page-error-500` pageView
      // operation, so it was hard to find. We therefore wrap the failure in a
      // real `Error` with a stable, greppable message + custom props, and ALSO
      // emit a structured `api_5xx` event so 5xx are trivially countable in KQL.
      // Emitting HERE, before any navigate, guarantees capture regardless of the
      // operation rename (spec 034 observability lineage).
      if (SERVER_ERROR_STATUSES.includes(error.status)) {
        const serverError = new Error(`HTTP ${error.status} ${req.method} ${req.url}`);
        serverError.name = 'ApiServerError';
        const telemetryProps = {
          source: 'httpErrorInterceptor:5xx',
          serverError: true,
          status: error.status,
          statusText: error.statusText,
          url: req.url,
          method: req.method,
          correlationId: telemetryContext.correlationId,
          backendErrorCode: extractBackendErrorCode(error),
          silent,
          headers: redactHeaders(req.headers)
        };
        // Real Error instance => consistent, queryable exception signature.
        appInsightsService.trackException(serverError, 3, telemetryProps);
        // Structured, trivially-countable event for KQL dashboards/alerts.
        appInsightsService.trackEvent('api_5xx', telemetryProps);
      } else {
        appInsightsService.trackException(
          error,
          3,
          {
            name: error?.name || 'UnknownError',
            message: error?.message,
            source: 'httpErrorInterceptor',
            request: {
              url: req.url,
              method: req.method,
              headers: redactHeaders(req.headers)
            }
          }
        );
      }

      // Mark the error as handled to avoid duplicate logging.
      (error as any).__handled = true;
      console.error('HTTP Error:', error);

      // Handle specific HTTP error status codes
      switch (error.status) {
        // 4xx Client Errors
        case 401:
          // Unauthorized - token is invalid, expired, or rejected by backend
          console.warn('[httpErrorInterceptor] 401 Unauthorized - logging out user');
          appInsightsService.trackTrace(
            'API returned 401 Unauthorized',
            { url: req.url, method: req.method }
          );
          auth0.logout(nativeLogout({ logoutParams: { returnTo: window.location.origin + '/login' } }));
          // Clear the native app-icon badge on forced logout.
          void appBadge.clear();
          if (isNativeApp()) {
            // Local logout on native has no browser redirect; route to login manually.
            router.navigate(['/login']);
          }
          return EMPTY;

        case 403:
          // Spec 051 FR-004: record which machine-readable status delivered the
          // caregiver here (never the raw URL/body — FR-011).
          router.navigate(['/page-error-forbidden-403'], {
            state: { [ERROR_SCREEN_ARRIVAL_REASON_STATE_KEY]: `http-${error.status}` },
          });
          break;

        case 404:
          console.error('Received a 404 error', error);
          // Special case: User profile 404 means user doesn't exist - logout
          if (req.url.includes('/user/profile')) {
            console.error('User not found, logging out and redirecting to login page');
            auth0.logout(nativeLogout());
            void appBadge.clear();
            router.navigate(['/login']);
          }
          // For all other API 404 errors, let the service's catchError handle it
          // Don't redirect to 404 page - that's only for navigation/route errors
          // The calling component should display an appropriate error message
          break;

        case 422:
          router.navigate(['/page-error-422'], {
            state: { [ERROR_SCREEN_ARRIVAL_REASON_STATE_KEY]: `http-${error.status}` },
          });
          break;

        // 5xx Server Errors
        // Telemetry is already recorded above. Redirect to the full error page —
        // UNLESS this request opted in via SILENT_5XX (e.g. the unread-count
        // badge poll) and its caller recovers on its own. Hijacking such a
        // failure with a full-page redirect yanked the user to /page-error-500
        // for a transient cold-start 504 the service intended to swallow, and
        // (under enableAutoRouteTracking) renamed the operation for
        // otherwise-healthy telemetry. Silent requests just rethrow for the
        // caller to handle.
        case 500:
        case 502:
        case 503:
        case 504:
          if (!silent) {
            router.navigate(['/page-error-500'], {
              state: { [ERROR_SCREEN_ARRIVAL_REASON_STATE_KEY]: `http-${error.status}` },
            });
          }
          break;

        default:
          break;
      }
      return throwError(() => error);
    })
  );
};

// Helper function to redact sensitive headers
function redactHeaders(headers: HttpHeaders): { [key: string]: string } {
  const result: { [key: string]: string } = {};
  headers.keys().forEach(key => {
    const value = headers.get(key) || '';
    if (key.toLowerCase() === 'authorization') {
      result[key] = '[REDACTED]';
    } else {
      result[key] = value;
    }
  });
  return result;
}

/**
 * Extract ONLY the structured backend error code from a failing response.
 * Machine codes (e.g. `INTERNAL_ERROR`) are safe to log; the raw response
 * body/message is intentionally NOT included as it may carry PII/PHI or tokens.
 */
function extractBackendErrorCode(error: HttpErrorResponse): string {
  const code = (error?.error as { error?: unknown } | null)?.error;
  return typeof code === 'string' ? code : '';
}